import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Collapse, IconButton, Paper, Typography } from "@mui/material"
import { AddOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import { useParams, useSearch } from "@tanstack/react-router"
import React, { type ReactElement, useCallback, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation } from "urql"
import type { z } from "zod"

import { useProductShipping } from "../hooks/useProductShipping"

import AddShippingDetailsModal from "./AddShippingDetailsModal"
import { ShippingDetailsSchema } from "./ShippingDetailsSchema"
import ShippingDetailsTable from "./ShippingDetailsTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import {
  LeadTimeUnits,
  type Mutation,
  type ProductMutationUpdateArgs,
  type ProductUpdateFailure,
  UpdateProductMutationDocument,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { calculateLeadTimeDisplayValue, calculateLeadTimeInDays } from "@/screens/Products/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

type FormData = z.infer<typeof ShippingDetailsSchema>

const defaultShippingDetails = {
  weightPerTruckload: null,
  leadTimeInDays: null,
  minOrderQuantity: null,
  leadTimeDisplayUnits: LeadTimeUnits.Days,
}

export const ProductShippingDetailsContainer = ({
  isPastProductVersion,
}: {
  isPastProductVersion: boolean
}): ReactElement => {
  const [shippingDetailsModal, setShippingDetailsModal] = useState(false)
  const { enqueueNotification } = useNotificationsStore()
  const methods = useForm<FormData>({
    defaultValues: defaultShippingDetails,
    resolver: zodResolver(ShippingDetailsSchema),
    mode: "onChange",
  })
  const { productId: queryCompanyId } = useParams({
    from: "/app/product/$productId/edit",
  })

  const { version } = useSearch({ from: "/app/product/$productId/edit/_layout/information" })

  const { data: productShipping, fetch: fetchProductShipping } = useProductShipping({
    productId: Number(queryCompanyId),
    version: version,
  })

  const [, updateProduct] = useMutation<Pick<Mutation, "product">, ProductMutationUpdateArgs>(
    UpdateProductMutationDocument
  )

  const [expanded, setExpanded] = useState(true)

  const { setError } = methods

  const showShippingDetails =
    productShipping?.weightPerTruckload || productShipping?.leadTimeInDays || productShipping?.minOrderQuantity

  const handleSubmit = useCallback(
    async (data: FormData) => {
      const { leadTimeDisplayUnits, leadTimeInDays, ...restData } = data
      const leadTimeInDaysValue = leadTimeInDays ? calculateLeadTimeInDays(leadTimeInDays, leadTimeDisplayUnits) : null

      const { error, data: result } = await updateProduct({
        input: {
          ...restData,
          productId: Number(queryCompanyId),
          versionNumber: version,
          weightPerTruckload: Number(data.weightPerTruckload) || null,
          leadTimeInDays: leadTimeInDaysValue,
          leadTimeDisplayUnits,
          minOrderQuantity: Number(data.minOrderQuantity) || null,
        },
      })
      if (error || (result?.product.update as ProductUpdateFailure)?.error) {
        setError("root", {
          // Keep this as "name"
          type: "server",
          message: error?.message || (result?.product?.update as ProductUpdateFailure)?.error?.message,
        })

        enqueueNotification({
          type: NotificationType.Error,
          silent: false,
          content: <p>Failed to {showShippingDetails ? "update" : "add"} product supplier shipping details</p>,
        })
      } else {
        enqueueNotification({
          type: NotificationType.Success,
          silent: false,
          content: <p>Product supplier shipping details successfully {showShippingDetails ? "updated" : "added"}.</p>,
        })
      }
      methods.reset()
      setShippingDetailsModal(false)
      fetchProductShipping({ productId: Number(queryCompanyId) })
    },
    [
      enqueueNotification,
      fetchProductShipping,
      methods,
      queryCompanyId,
      setError,
      showShippingDetails,
      updateProduct,
      version,
    ]
  )

  const onEditHandleClick = useCallback(() => {
    methods.reset({
      weightPerTruckload: productShipping?.weightPerTruckload,
      leadTimeInDays: calculateLeadTimeDisplayValue(
        productShipping?.leadTimeInDays,
        productShipping?.leadTimeDisplayUnits || LeadTimeUnits.Days
      ),
      minOrderQuantity: productShipping?.minOrderQuantity,
      leadTimeDisplayUnits: productShipping?.leadTimeDisplayUnits || LeadTimeUnits.Days,
    })
    setShippingDetailsModal(true)
  }, [methods, productShipping])

  return (
    <Paper className='border border-gray-300 p-6' elevation={0}>
      <Box className='flex items-center justify-between'>
        <Typography variant='h6' className={expanded ? "mb-6" : ""}>
          Supplier Shipping Details
        </Typography>
        <IconButton onClick={() => setExpanded(!expanded)} aria-expanded={expanded} aria-label='show more'>
          {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        {showShippingDetails ? (
          <ShippingDetailsTable
            productDetails={productShipping}
            onEditConditionClick={onEditHandleClick}
            isPastProductVersion={isPastProductVersion}
          />
        ) : (
          <InformationMissingAlert
            title='Add Supplier Shipping Details'
            description=''
            {...(!isPastProductVersion && {
              action: (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  data-testid='add-supplier-shipping-details'
                  startIcon={<AddOutlined />}
                  onClick={() => {
                    methods.reset(defaultShippingDetails)
                    setShippingDetailsModal(true)
                  }}
                >
                  Supplier Shipping Details
                </Button>
              ),
            })}
          />
        )}
      </Collapse>
      <FormProvider {...methods}>
        <AddShippingDetailsModal
          open={shippingDetailsModal}
          onClose={() => {
            methods.reset()
            setShippingDetailsModal(false)
          }}
          onSubmit={handleSubmit}
        />
      </FormProvider>
    </Paper>
  )
}
